<template>
  <aside class="side-panel" :class="{ show: panelType === 'projectInfo' }">
    <div id="info-container" ref="infoContainer" class="row">
      <div
        id="sideButton-Container"
        ref="sideButtonContainer"
        :class="{ show: panelType === 'projectInfo' }"
      >
        <div id="side-buttons" class="">
          <div class="timeline-nav">
            <div
              class="timeline-item"
              @click="scrollToTarget('scroll-target-1')"
            >
              <div class="timeline-dot"></div>
              <span class="timeline-label">Background</span>
            </div>
            <div
              class="timeline-item"
              @click="scrollToTarget('scroll-target-2')"
            >
              <div class="timeline-dot"></div>
              <span class="timeline-label">Description</span>
            </div>
            <div
              class="timeline-item"
              @click="scrollToTarget('scroll-target-3')"
            >
              <div class="timeline-dot"></div>
              <span class="timeline-label">Benefits</span>
            </div>
          </div>
        </div>
      </div>
      <div id="infoContainer" tabindex="0">
        <div class="section" id="Background">
          <div id="scroll-target-1" class="scroll-target"></div>
          <h2 class="section-title">Background</h2>
          <div class="content">
            <div v-html="backgroundContent"></div>
          </div>
        </div>
        <hr class="section-divider" />
        <div class="section" id="Description">
          <div id="scroll-target-2" class="scroll-target"></div>
          <h2 class="section-title">Description</h2>
          <div class="content">
            <div v-html="descriptionContent"></div>
          </div>
        </div>
        <hr class="section-divider" />
        <div class="section" id="Benefits">
          <div id="scroll-target-3" class="scroll-target"></div>
          <h2 class="section-title">Benefits of the Project</h2>
          <div class="content">
            <div v-html="benefitsContent"></div>
          </div>
        </div>
      </div>
      <div id="goToMap">
        <router-link to="/eia">
          <div>EXPLORE MAP ➜</div>
        </router-link>
      </div>
    </div>
  </aside>
</template>

<script>
import { computed, ref, onMounted, onUnmounted } from "vue";
import Gumshoe from "gumshoejs";

const backgroundContent = ref("");
const descriptionContent = ref("");
const benefitsContent = ref("");

// Fetch the content of background.html
fetch("/text/background.html")
  .then((response) => response.text())
  .then((html) => {
    backgroundContent.value = html;
  })
  .catch((error) => {
    console.error("Error loading background content:", error);
    backgroundContent.value = "Failed to load background content.";
  });

// Fetch the content of description.html
fetch("/text/description.html")
  .then((response) => response.text())
  .then((html) => {
    descriptionContent.value = html;
  })
  .catch((error) => {
    console.error("Error loading description content:", error);
    descriptionContent.value = "Failed to load description content.";
  });

// Fetch the content of benefits.html
fetch("/text/benefits.html")
  .then((response) => response.text())
  .then((html) => {
    benefitsContent.value = html;
  })
  .catch((error) => {
    console.error("Error loading benefits content:", error);
    benefitsContent.value = "Failed to load benefits content.";
  });

export default {
  name: "SidePanelInfo",
  props: {
    panelType: String,
  },
  setup(props) {
    const infoContainer = ref(null);
    const sideButtonContainer = ref(null);
    const adjustHeight = () => {
      if (infoContainer.value && sideButtonContainer.value) {
        sideButtonContainer.value.style.height = `${infoContainer.value.clientHeight}px`;
        sideButtonContainer.value.style.left = `calc(-${sideButtonContainer.value.clientWidth}px + 15px)`;
      }
    };
    const panelTitle = computed(() => {
      if (props.panelType === "projectInfo") {
        return "Project Info";
      } else if (props.panelType === "environmentalImpacts") {
        return "Environmental Impacts";
      }
      return "";
    });

    let gumshoeInstance = null;

    const scrollToTarget = (targetId) => {
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    };

    onMounted(() => {
      adjustHeight();
      window.addEventListener("resize", adjustHeight);

      // Initialize Gumshoe ScrollSpy
      setTimeout(() => {
        const navLinks = document.querySelectorAll(
          "#side-buttons .timeline-item"
        );

        if (navLinks.length > 0) {
          // Create a custom Gumshoe implementation for window scroll
          let activeLink = null;

          const updateActiveState = () => {
            const scrollTop =
              window.pageYOffset || document.documentElement.scrollTop;

            // Get all target elements
            const targets = [
              {
                id: "scroll-target-1",
                element: document.querySelector("#scroll-target-1"),
              },
              {
                id: "scroll-target-2",
                element: document.querySelector("#scroll-target-2"),
              },
              {
                id: "scroll-target-3",
                element: document.querySelector("#scroll-target-3"),
              },
            ];

            let currentActive = null;
            let minDistance = Infinity;

            // Find which section is closest to the top of the viewport
            targets.forEach((target, index) => {
              if (target.element) {
                const rect = target.element.getBoundingClientRect();
                const elementTop = rect.top + scrollTop;

                // Check if element is in viewport with offset (500px as in original Bootstrap config)
                const distanceFromTop = Math.abs(scrollTop + 500 - elementTop);

                if (
                  scrollTop + 500 >= elementTop &&
                  distanceFromTop < minDistance
                ) {
                  currentActive = index;
                  minDistance = distanceFromTop;
                }
              }
            });

            // Fallback: if nothing is active, activate the first one
            if (currentActive === null && scrollTop < 500) {
              currentActive = 0;
            }

            console.log(
              "Window ScrollTop:",
              scrollTop,
              "Active:",
              currentActive
            );

            // Update active states
            navLinks.forEach((link, index) => {
              if (index === currentActive) {
                if (activeLink !== link) {
                  // Remove active from previous
                  if (activeLink) {
                    activeLink.classList.remove("active");
                  }
                  // Add active to current
                  link.classList.add("active");
                  activeLink = link;
                  console.log("Activated link:", index);
                }
              } else {
                link.classList.remove("active");
              }
            });
          };

          // Listen to scroll events on the window
          window.addEventListener("scroll", updateActiveState);

          // Initial check
          updateActiveState();

          // Store cleanup function
          gumshoeInstance = {
            destroy: () => {
              window.removeEventListener("scroll", updateActiveState);
            },
          };
        }
      }, 100);
    });
    onUnmounted(() => {
      window.removeEventListener("resize", adjustHeight);
      // Clean up Gumshoe instance
      if (gumshoeInstance) {
        gumshoeInstance.destroy();
      }
    });

    return {
      panelTitle,
      infoContainer,
      sideButtonContainer,
      backgroundContent,
      descriptionContent,
      benefitsContent,
      scrollToTarget,
    };
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box !important;
}
.side-panel {
  display: flex !important;
  flex-direction: row;
  position: absolute;
  top: 0;
  right: 0vw;
  width: auto;
  /* height: 100vh; */
  transition: transform 0.3s ease-in-out;
  background-color: none;
  transform: translateX(110%); /* Initially off-screen to the right */
  /* align-content: center; */
  justify-content: right;
}

.side-panel.show {
  /* display: block; */
  transform: translateX(0); /* Slide in to its natural position */
}

#info-container {
  box-sizing: border-box !important;
  padding: 8vw 2vw 0 2vw !important;
  width: 30vw;
  height: auto !important;
  /* overflow: scroll; */
  background-color: rgb(255, 255, 255);
}

/* When the panel is shown (ProjectInfo), reduce the large top padding
   so the content starts near the top without a big blank area. */
.side-panel.show #info-container {
  padding: 12vh 2vw 2vw 4vw;
}

#sideButton-Container {
  /* transform: translateX(100vw);  */
  position: fixed !important;
  left: 0px; /* Adjusted to align with the container */
  top: 0vh;
  height: 100% !important;
  width: auto; /* Set a fixed width for the side buttons */
  /* background-color: rgba(148, 148, 148, 0); */
  transition: transform 0.5s ease-in-out, opacity 0.3s ease-in-out;
  z-index: -100;
  padding: 0px;
  opacity: 0;
  pointer-events: none;
}

#sideButton-Container.show {
  transform: translateX(0); /* Slide in to its natural position */
  opacity: 1;
  pointer-events: auto;
  transition: transform 0.5s ease-in-out, opacity 0.5s ease-in-out;
}

#side-buttons {
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 50%;
  transform: translateY(-50%);
  padding-right: 0px;
  background-color: rgba(255, 255, 255, 0);
  font: 2vmin sans-serif;
  color: #dfdfdf;
}

.timeline-nav {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: rgb(255, 255, 255);
  border-radius: 20px;
  padding: 20px 10px 20px 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.timeline-nav::before {
  content: "";
  position: absolute;
  left: 50%;
  top: 40px;
  bottom: 40px;
  width: 2px;
  background-color: #ccc;
  transform: translateX(-50%);
}

.timeline-item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  text-decoration: none;
  margin: 15px 0;
  z-index: 1;
  width: 100%;
  cursor: pointer;
}

.timeline-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #ccc;
  border: 2px solid white;
  box-shadow: 0 0 0 2px #ccc;
  transition: all 0.3s ease;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.timeline-item.active .timeline-dot {
  background-color: #00353e;
  box-shadow: 0 0 0 2px #00353e;
}

.timeline-item.active .timeline-label {
  color: #00353e;
  font-weight: 600;
}

.timeline-item:hover .timeline-dot {
  background-color: #008768;
  box-shadow: 0 0 0 2px #008768;
}

.timeline-label {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  font-size: 14px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  margin-right: 50px;
}

.section-title {
  font-family: Arial, Helvetica, sans-serif;
  font-size: 2vw;
  padding-top: 4vh;
  color: #008768;
}

.content {
  font-family: Arial, Helvetica, sans-serif;
  font-size: calc(0.6rem + 0.6vw);
  padding-top: 2vh;
  line-height: 1.5;
}

.section {
  padding-top: 0vh;
  /* position: relative; */
}

a {
  text-decoration: none !important;
}

.scroll-target {
  position: relative;
  /* top: -12vh; */
  scroll-margin-top: 12vh;
}

#goToMap {
  position: sticky;
  /* margin-top: auto; */
  bottom: 0 !important;
  width: 100%;
  padding: 4vw 2vw 2vw 12px;
  /* gradient background color */
  background: linear-gradient(
    0deg,
    rgb(255, 255, 255) 70%,
    rgba(255, 255, 255, 0) 100%
  );
}

#goToMap div {
  /* rounded corner green button */
  background-color: #008768;
  color: white;
  width: 179px;
  height: 46px;
  margin-right: auto;
  padding: 10px;
  text-align: center;
  cursor: pointer;
  border-radius: 20px;
  font: 1rem sans-serif;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
}

#goToMap div:hover {
  background-color: #00606a;
}

.section-divider {
  border: 1px solid #000000;
  margin: 4vh 0 0 0;
}
</style>
